// src/components/TeamItemsPage/KanbanView.jsx

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './KanbanView.module.css';
import { FaEdit, FaTrash, FaEye, FaFlag, FaCalendarAlt, FaUser } from 'react-icons/fa';
import { format } from 'date-fns';
import axios from 'axios';

const KanbanView = ({ items, onItemUpdated, onEdit, onDelete, onQuickView, canManage }) => {
  const [draggingItem, setDraggingItem] = useState(null);

  // Group items by status
  const columns = {
    'To Do': items.filter(item => item.status === 'To Do'),
    'In Progress': items.filter(item => item.status === 'In Progress'),
    'Done': items.filter(item => item.status === 'Done')
  };

  const handleDragStart = (e, item) => {
    setDraggingItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    
    if (!draggingItem || draggingItem.status === newStatus || !canManage) {
      setDraggingItem(null);
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('token') && {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          })
        }
      };

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${draggingItem._id}`,
        { status: newStatus },
        config
      );
      
      onItemUpdated(response.data);
    } catch (err) {
      alert('Failed to update assignment status.');
    } finally {
      setDraggingItem(null);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return '#dc3545';
      case 'Medium':
        return '#ffc107';
      case 'Low':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  const renderCard = (item) => (
    <div
      key={item._id}
      className={styles.kanbanCard}
      draggable={canManage}
      onDragStart={(e) => handleDragStart(e, item)}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>{item.title}</div>
        <div
          className={styles.priorityBadge}
          style={{ backgroundColor: getPriorityColor(item.priority) }}
        >
          <FaFlag /> {item.priority}
        </div>
      </div>

      {item.description && (
        <div className={styles.cardDescription}>
          {item.description.slice(0, 100)}
          {item.description.length > 100 && '...'}
        </div>
      )}

      <div className={styles.cardMeta}>
        {item.dueDate && (
          <div className={styles.metaItem}>
            <FaCalendarAlt />
            <span>{format(new Date(item.dueDate), 'MMM dd')}</span>
          </div>
        )}
        {item.assignedTo && (
          <div className={styles.metaItem}>
            <FaUser />
            <span>
              {item.assignedTo.firstName} {item.assignedTo.lastName}
            </span>
          </div>
        )}
      </div>

      <div className={styles.cardActions}>
        <button
          className={styles.actionBtn}
          onClick={() => onQuickView(item)}
          title="Quick View"
        >
          <FaEye />
        </button>
        {canManage && (
          <>
            <button
              className={styles.actionBtn}
              onClick={() => onEdit(item)}
              title="Edit"
            >
              <FaEdit />
            </button>
            <button
              className={`${styles.actionBtn} ${styles.deleteBtn}`}
              onClick={() => onDelete(item._id)}
              title="Delete"
            >
              <FaTrash />
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.kanbanBoard}>
      {Object.entries(columns).map(([status, columnItems]) => (
        <div
          key={status}
          className={styles.kanbanColumn}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, status)}
        >
          <div className={styles.columnHeader}>
            <h3 className={styles.columnTitle}>{status}</h3>
            <span className={styles.columnCount}>{columnItems.length}</span>
          </div>
          <div className={styles.columnContent}>
            {columnItems.length === 0 ? (
              <div className={styles.emptyColumn}>
                {canManage ? 'Drag items here' : 'No items'}
              </div>
            ) : (
              columnItems.map(item => renderCard(item))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

KanbanView.propTypes = {
  items: PropTypes.array.isRequired,
  onItemUpdated: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onQuickView: PropTypes.func.isRequired,
  canManage: PropTypes.bool
};

export default KanbanView;

